import { ArrowBack, HomeOutlined } from '@mui/icons-material';
import { Button, Paper, Stack, ThemeProvider, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import Result from 'components/v2/common/Result';
import PageBackground from 'components/v2/PageBackground';

import appThemeV2 from 'theme/themeV2';

export interface ErrorPageLayoutProps {
    icon: ReactNode;
    title: string;
    description: string;
    /** Soft tint for the icon well. Defaults to primary indigo. */
    iconTone?: 'primary' | 'warning' | 'error';
}

const iconToneStyles: Record<
    NonNullable<ErrorPageLayoutProps['iconTone']>,
    { color: string; background: string; ring: string }
> = {
    primary: {
        color: '#4f46e5',
        background:
            'linear-gradient(145deg, rgba(99, 102, 241, 0.16) 0%, rgba(99, 102, 241, 0.06) 100%)',
        ring: 'rgba(99, 102, 241, 0.16)',
    },
    warning: {
        color: '#d97706',
        background:
            'linear-gradient(145deg, rgba(245, 158, 11, 0.18) 0%, rgba(245, 158, 11, 0.06) 100%)',
        ring: 'rgba(245, 158, 11, 0.16)',
    },
    error: {
        color: '#dc2626',
        background:
            'linear-gradient(145deg, rgba(239, 68, 68, 0.16) 0%, rgba(239, 68, 68, 0.06) 100%)',
        ring: 'rgba(239, 68, 68, 0.16)',
    },
};

const primaryButtonSx: SxProps<Theme> = {
    py: { xs: 1.35, sm: 1.25 },
    px: 2.5,
    borderRadius: 1.5,
    fontWeight: 600,
    textTransform: 'none',
    fontSize: { xs: '1rem', sm: '0.9375rem' },
    boxShadow: 'none',
    width: { xs: '100%', sm: 'auto' },
    minWidth: { sm: 160 },
    flex: { sm: '1 1 0' },
    background: (theme) => theme.palette.primary.main,
    '&:hover': {
        background: (theme) => theme.palette.primary.dark,
        boxShadow: '0 8px 20px rgba(99, 102, 241, 0.28)',
    },
};

const secondaryButtonSx: SxProps<Theme> = {
    py: { xs: 1.35, sm: 1.25 },
    px: 2.5,
    borderRadius: 1.5,
    fontWeight: 600,
    textTransform: 'none',
    fontSize: { xs: '1rem', sm: '0.9375rem' },
    width: { xs: '100%', sm: 'auto' },
    minWidth: { sm: 160 },
    flex: { sm: '1 1 0' },
    borderColor: 'rgba(148, 163, 184, 0.5)',
    color: 'text.primary',
    '&:hover': {
        borderColor: 'primary.main',
        backgroundColor: 'rgba(99, 102, 241, 0.04)',
    },
};

/**
 * Shared full-page shell for public error screens.
 * Uses the v2 Result card inside an auth-style PageBackground + Paper frame.
 */
export default function ErrorPageLayout({
    icon,
    title,
    description,
    iconTone = 'primary',
}: ErrorPageLayoutProps) {
    const navigate = useNavigate();
    const tone = iconToneStyles[iconTone];

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground
                maxWidth={false}
                rootSx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                sx={{
                    mt: 0,
                    mb: 0,
                    minHeight: '100dvh',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 0, sm: 3 },
                    px: { xs: 0, sm: 2 },
                    boxSizing: 'border-box',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: { xs: '100%', sm: 460 },
                        minHeight: { xs: '100dvh', sm: 'auto' },
                        maxHeight: { xs: '100dvh', sm: 'none' },
                        mx: 'auto',
                        px: { xs: 2.5, sm: 4 },
                        py: {
                            xs: 'max(2rem, env(safe-area-inset-top, 0px))',
                            sm: 4.5,
                        },
                        pb: {
                            xs: 'max(2rem, env(safe-area-inset-bottom, 0px))',
                            sm: 4.5,
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        overflowY: { xs: 'auto', sm: 'visible' },
                        borderRadius: { xs: 0, sm: 2.5 },
                        border: 'none',
                        bgcolor: 'background.paper',
                        boxShadow: {
                            xs: 'none',
                            sm: '0 0 8px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
                        },
                        boxSizing: 'border-box',
                    }}
                >
                    <Stack
                        spacing={{ xs: 2.5, sm: 3 }}
                        sx={{
                            alignItems: 'center',
                            width: '100%',
                            maxWidth: { xs: '100%', sm: 380 },
                            mx: 'auto',
                            textAlign: 'center',
                        }}
                    >
                        <Result
                            icon={icon}
                            title={title}
                            description={description}
                            sx={{
                                width: '100%',
                                border: 'none',
                                background: 'transparent',
                                px: 0,
                                py: 0,
                                // Restyle Result's built-in icon well for this page.
                                '& .MuiStack-root > .MuiStack-root:first-of-type': {
                                    width: { xs: 68, sm: 76 },
                                    height: { xs: 68, sm: 76 },
                                    borderRadius: { xs: '20px', sm: '24px' },
                                    color: tone.color,
                                    background: tone.background,
                                    boxShadow: `0 0 0 8px ${tone.ring}`,
                                    mb: 0.5,
                                    flexShrink: 0,
                                },
                                '& .MuiTypography-h6': {
                                    fontSize: { xs: '1.25rem', sm: '1.375rem' },
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em',
                                    color: 'text.primary',
                                    px: { xs: 0.5, sm: 0 },
                                },
                                '& .MuiTypography-root:not(.MuiTypography-h6)': {
                                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                    lineHeight: 1.65,
                                    maxWidth: { xs: '100%', sm: 340 },
                                    mx: 'auto',
                                    px: { xs: 0.5, sm: 0 },
                                    wordBreak: 'break-word',
                                },
                            }}
                        />

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            sx={{
                                width: '100%',
                                justifyContent: 'center',
                                alignItems: 'stretch',
                                pt: { xs: 0.5, sm: 0.5 },
                            }}
                        >
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<ArrowBack />}
                                onClick={() => navigate(-1)}
                                sx={primaryButtonSx}
                            >
                                Go back
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<HomeOutlined />}
                                onClick={() => navigate('/')}
                                sx={secondaryButtonSx}
                            >
                                Home
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            </PageBackground>
        </ThemeProvider>
    );
}
